import type { ServedCta } from 'ProdantixSdk/core/ctas';
import { FORM_STRINGS, type FormLanguage } from 'ProdantixSdk/web/forms/strings';

export interface RenderCtaOptions {
  language: FormLanguage;
  /** A button was pressed, by its index in the variant's buttons. */
  onButton: (index: number) => void;
  /** The visitor closed the CTA without pressing a button. */
  onDismiss: () => void;
}

export interface RenderedCta {
  destroy(): void;
  /** The element each copy of the CTA was drawn into. */
  elements: HTMLElement[];
}

const CLASS = 'prodantix-cta';

const STYLE_ID = 'prodantix-cta-style';

const STYLE = `.${CLASS}{font-family:system-ui,sans-serif;color:var(--pdx-text);background:var(--pdx-background);border-radius:var(--pdx-radius);box-sizing:border-box;padding:16px;display:flex;gap:12px;align-items:center;flex-wrap:wrap}
.${CLASS} *{box-sizing:border-box}
.${CLASS} h2{font-size:18px;line-height:1.3;margin:0;font-weight:700}
.${CLASS} p{margin:0;font-size:15px;line-height:1.5}
.${CLASS} img{max-width:100%;height:auto;border-radius:var(--pdx-radius)}
.${CLASS} .pdx-text{display:flex;flex-direction:column;gap:6px;flex:1 1 240px;min-width:0}
.${CLASS} .pdx-actions{display:flex;gap:8px;flex-wrap:wrap}
.${CLASS} button{font:inherit;min-height:44px;padding:0 18px;border:0;border-radius:var(--pdx-radius);background:var(--pdx-button);color:var(--pdx-button-text);cursor:pointer}
.${CLASS} button[data-secondary]{background:transparent;color:inherit;border:1px solid currentColor}
.${CLASS} .pdx-close{min-width:44px;padding:0;background:transparent;color:inherit;font-size:22px;line-height:1}
.${CLASS} .pdx-meta{font-size:12px;opacity:.85;flex-basis:100%}
.${CLASS} :focus-visible{outline:2px solid var(--pdx-button);outline-offset:2px}
.${CLASS}[data-kind=banner]{position:fixed;left:0;right:0;z-index:2147483000;border-radius:0}
.${CLASS}[data-kind=banner][data-placement=top]{top:0}
.${CLASS}[data-kind=banner][data-placement=bottom]{bottom:0}
.${CLASS}[data-kind=slide_in]{position:fixed;bottom:16px;z-index:2147483000;width:min(360px,calc(100vw - 32px));box-shadow:0 8px 32px rgba(0,0,0,.18)}
.${CLASS}[data-kind=slide_in][data-placement=bottom_left]{left:16px}
.${CLASS}[data-kind=slide_in][data-placement=bottom_right]{right:16px}
dialog.${CLASS}{border:0;max-width:min(480px,calc(100vw - 32px));box-shadow:0 16px 48px rgba(0,0,0,.24)}
dialog.${CLASS}::backdrop{background:rgba(0,0,0,.45)}
@media (prefers-reduced-motion:no-preference){.${CLASS}[data-kind=slide_in],.${CLASS}[data-kind=banner],dialog.${CLASS}{animation:pdx-cta-in .2s ease-out}}
@keyframes pdx-cta-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}`;

const ensureStyle = (): void => {
  if (document.getElementById(STYLE_ID)) {
    return;
  }
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = STYLE;
  document.head.append(style);
};

const paint = (element: HTMLElement, cta: ServedCta): void => {
  const style = cta.variant.style;
  for (const [name, value] of [
    ['--pdx-background', style.background],
    ['--pdx-text', style.text],
    ['--pdx-button', style.button],
    ['--pdx-button-text', style.buttonText],
    ['--pdx-radius', `${style.radius}px`],
  ] as const) {
    element.style.setProperty(name, value);
  }
};

const fill = (element: HTMLElement, cta: ServedCta, options: RenderCtaOptions, close: (() => void) | null): void => {
  const content = cta.variant.content;
  const strings = FORM_STRINGS[options.language];
  element.className = CLASS;
  element.dataset.kind = cta.kind;
  element.dataset.placement = cta.placement;
  element.dataset.ctaId = cta.id;
  paint(element, cta);
  if (content.image) {
    const image = document.createElement('img');
    image.src = content.image;
    image.alt = content.imageAlt;
    image.loading = 'lazy';
    element.append(image);
  }
  const text = document.createElement('div');
  text.className = 'pdx-text';
  if (content.heading) {
    const heading = document.createElement('h2');
    heading.textContent = content.heading;
    text.append(heading);
  }
  if (content.body) {
    const body = document.createElement('p');
    body.textContent = content.body;
    text.append(body);
  }
  element.append(text);
  if (content.buttons.length > 0) {
    const actions = document.createElement('div');
    actions.className = 'pdx-actions';
    content.buttons.forEach((button, index) => {
      const control = document.createElement('button');
      control.type = 'button';
      control.textContent = button.label;
      control.dataset.action = `button-${index}`;
      if (index > 0) {
        control.dataset.secondary = '';
      }
      control.addEventListener('click', () => options.onButton(index));
      actions.append(control);
    });
    element.append(actions);
  }
  if (close) {
    const control = document.createElement('button');
    control.type = 'button';
    control.className = 'pdx-close';
    control.dataset.action = 'close';
    control.textContent = '×';
    control.setAttribute('aria-label', strings.close);
    control.title = strings.close;
    control.addEventListener('click', close);
    element.append(control);
  }
  if (cta.poweredBy) {
    const meta = document.createElement('p');
    meta.className = 'pdx-meta';
    meta.textContent = strings.poweredBy;
    element.append(meta);
  }
};

/** Draws a CTA as its kind asks: into each `[data-prodantix-cta]` slot named
 *  for it, as a banner, as a slide-in, or as a pop-up that holds focus until
 *  it closes and then hands focus back. */
export const renderCta = (cta: ServedCta, options: RenderCtaOptions): RenderedCta => {
  ensureStyle();
  const label = cta.variant.content.heading || cta.variant.content.body;
  if (cta.kind === 'embedded') {
    const slots = [...document.querySelectorAll<HTMLElement>('[data-prodantix-cta]')].filter(slot => slot.dataset.prodantixCta === cta.id);
    const elements = slots.map(slot => {
      const element = document.createElement('section');
      element.setAttribute('aria-label', label);
      fill(element, cta, options, null);
      slot.replaceChildren(element);
      return element;
    });
    return { destroy: () => elements.forEach(element => element.remove()), elements };
  }
  if (cta.kind === 'popup') {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = document.createElement('dialog');
    dialog.setAttribute('aria-label', label);
    let isClosed = false;
    const destroy = (): void => {
      if (isClosed) {
        return;
      }
      isClosed = true;
      if (dialog.open) {
        dialog.close();
      }
      dialog.remove();
      opener?.focus();
    };
    const dismiss = (): void => {
      destroy();
      options.onDismiss();
    };
    fill(dialog, cta, options, dismiss);
    dialog.addEventListener('cancel', event => {
      event.preventDefault();
      dismiss();
    });
    document.body.append(dialog);
    dialog.showModal();
    return { destroy, elements: [dialog] };
  }
  const element = document.createElement('section');
  element.setAttribute('aria-label', label);
  if (cta.kind === 'slide_in') {
    element.setAttribute('role', 'dialog');
    element.setAttribute('aria-modal', 'false');
  }
  const destroy = (): void => element.remove();
  fill(element, cta, options, () => {
    destroy();
    options.onDismiss();
  });
  document.body.append(element);
  return { destroy, elements: [element] };
};
